import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { message } from "antd";
import { Hooks, Request } from "@xt/xtbase";
import ProptradingLayout from "@/components/Pages/proptrading/ProptradingLayout";
import challengeCardIconRookieMaster from "@/assets/img/proptrading/prop-challenge-card-icon-rookie-master.png";
import challengeCardIconEliteLive from "@/assets/img/proptrading/prop-challenge-card-icon-elite-live.png";
import heroBg from "@/assets/img/proptrading/bg.png";
import rule1 from "@/assets/img/proptrading/rule1.png";
import rule2 from "@/assets/img/proptrading/rule2.png";
import rule3 from "@/assets/img/proptrading/rule3.png";
import leftArrow from "@/assets/img/proptrading/left.png";
import rightArrow from "@/assets/img/proptrading/right.png";
import { get_prop_home, PropHomeVO } from "@/api/v4/proptrading";
import {
  canEnterPropTradePage,
  mergePropStagesInOrder,
  propArenaLabelI18nKey,
  safeTrimString,
} from "@/utils/proptrading/propHomeUtils";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;

function imgSrc(mod: string | { src?: string }) {
  if (typeof mod === "string") return mod;
  return mod?.src ?? String(mod);
}

function pctChallengeRow(v: string | number | undefined | null): string {
  if (v == null || String(v).trim() === "") return "—";
  const s = String(v).trim();
  return s.includes("%") ? s : `${s}%`;
}

function durationHoursChallengeRow(hours: number | undefined | null, hoursUnit: string): string {
  if (hours == null || Number.isNaN(Number(hours))) return "—";
  return `${hours} ${hoursUnit}`;
}

/** 首页挑战卡门票价，默认 $ 前缀（与详情页一致） */
function formatChallengeCardTicketPrice(price: string): string {
  const s = String(price).trim();
  if (!s) return "—";
  const core = s.replace(/^\$\s*/, "").trim();
  return `$${core}`;
}

/**
 * Proptrading home page - i18n enabled，GET /prop/home 联调
 */
const ProptradingHomePage: React.FC = () => {
  const t = useTranslation();
  const router = useRouter();
  const [home, setHome] = useState<PropHomeVO | null>(null);
  const [homeLoaded, setHomeLoaded] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  const orderedStages = useMemo(() => mergePropStagesInOrder(home?.stages), [home?.stages]);

  // 根据用户当前场次计算默认显示的卡片索引
  const getDefaultCardIndex = useCallback(() => {
    const userStage = home?.userStage;
    if (userStage && orderedStages.length > 0) {
      const index = orderedStages.findIndex(
        (stage) => String(stage.stage ?? "").trim().toLowerCase() === String(userStage).trim().toLowerCase()
      );
      if (index !== -1) return index;
    }
    return 0; // 默认显示第一个（新手场）
  }, [home?.userStage, orderedStages]);

  // 数据加载后设置默认卡片索引
  useEffect(() => {
    if (homeLoaded && orderedStages.length > 0) {
      setCurrentCardIndex(getDefaultCardIndex());
    }
  }, [homeLoaded, orderedStages.length]);

  useEffect(() => {
    let cancelled = false;
    setHomeLoaded(false);
    (async () => {
      const { err, data } = await Request.XtAxios4.asyncAwait(get_prop_home);
      if (cancelled) return;
      if (err) {
        setHome(null);
      } else {
        setHome((data as PropHomeVO) || null);
      }
      setHomeLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onCtaClick = useCallback(() => {
    if (!homeLoaded) {
      message.info(t("proptrading.ctaLoading"));
      return;
    }
    if (!canEnterPropTradePage(home)) {
      message.warning(t("proptrading.ctaSelectChallengeFirst"));
      return;
    }
    router.push("/proptrading/trade/btc_usdx_cfd");
  }, [home, homeLoaded, router, t]);

  const heroBadge2Text =
    home?.currentStageName && String(home.currentStageName).trim() !== ""
      ? String(home.currentStageName)
      : t("proptrading.heroBadge2");

  return (
    <ProptradingLayout title="Prop Trading" activeTab="home">
      <div className={styles.home}>
        <section className={styles.hero}>
          <img src={heroBg} alt="" className={styles.heroBgImg} />
          <p className={styles.heroTitle}>{t("proptrading.heroTitle")}</p>
          <div className={styles.heroBadge}>
            <span className={styles.heroBadgeHighlight}>{t("proptrading.heroBadgeHighlight")}</span>
            <span className={styles.heroBadgeGreen}>{t("proptrading.heroBadgeGreen")}</span>
          </div>
          <div className={styles.heroBadge2}>{heroBadge2Text}</div>
          <p className={styles.heroDesc1}>{t("proptrading.heroDesc1")}</p>
          <p className={styles.heroDesc2}>{t("proptrading.heroDesc2")}</p>
        </section>

        <button type="button" className={styles.ctaBtn} onClick={onCtaClick}>
          {t("proptrading.ctaBtn")}
        </button>

        <section className={styles.statsCard}>
          <div className={styles.statsItem}>
            <p className={styles.statsValue}>{t("proptrading.statsValue1")}</p>
            <p className={styles.statsLabel}>{t("proptrading.statsLabel1")}</p>
          </div>
          <div className={styles.statsItem}>
            <p className={styles.statsValue}>{t("proptrading.statsValue2")}</p>
            <p className={styles.statsLabel}>{t("proptrading.statsLabel2")}</p>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <p className={styles.sectionTitleMain}>{t("proptrading.sectionTitleChallenge")}</p>
            <p className={styles.sectionTitleSub}>{t("proptrading.sectionSubChallenge")}</p>
          </div>
          <div className={styles.challengeCarousel}>
            {/* 左侧切换箭头 */}
            <button
              type="button"
              className={`${styles.carouselArrow} ${styles.carouselArrowLeft}`}
              onClick={() => setCurrentCardIndex((prev) => (prev > 0 ? prev - 1 : prev))}
              disabled={currentCardIndex === 0}
            >
              <img src={leftArrow} alt="previous" />
            </button>

            {/* 中间竞技场卡片 */}
            <div className={styles.challengeScroll}>
              {orderedStages.map((stage, i) => {
                if (i !== currentCardIndex) return null;
                const stageU = String(stage.stage ?? "").trim().toUpperCase();
                const iconSrc =
                  stageU === "ELITE" || stageU === "LIVE"
                    ? imgSrc(challengeCardIconEliteLive)
                    : imgSrc(challengeCardIconRookieMaster);
                const key = `stage-${stage.stage ?? "x"}-${i}`;
                const headSpan =
                  stageU === "LIVE"
                    ? t("proptrading.liveTrading")
                    : t("proptrading.simulatedTrading");
                const capitalLabel =
                  stageU === "LIVE"
                    ? t("proptrading.arena.detail.liveCapital")
                    : t("proptrading.simulatedCapital");
                const stageNameLine = safeTrimString(stage.stageName);
                const amountLine = stageNameLine
                  ? stageNameLine
                  : t(propArenaLabelI18nKey(stage.stage));
                const stageCode =
                  stage.stage != null && String(stage.stage).trim() !== ""
                    ? String(stage.stage).trim().toUpperCase()
                    : "";
                const btnLabel = stageCode || "—";
                const rules = stage.rules;
                const profitTargetDisplay = pctChallengeRow(rules?.profitTargetPercent);
                const maxDrawdownDisplay = pctChallengeRow(rules?.maxDrawdownPercent);
                const timeLimitDisplay = durationHoursChallengeRow(
                  rules?.durationHours,
                  t("proptrading.arena.detail.hoursUnit"),
                );
                const rawTicket =
                  stage.ticket?.price != null && String(stage.ticket.price).trim() !== ""
                    ? String(stage.ticket.price).trim()
                    : "";
                const challengePriceDisplay = rawTicket
                  ? formatChallengeCardTicketPrice(rawTicket)
                  : "—";
                const onStageBtnClick = () => {
                  const s = safeTrimString(stage.stage);
                  if (!s) return;
                  router.push(`/proptrading/arena/${encodeURIComponent(s)}`);
                };

                return (
                  <div key={key} className={styles.challengeCard}>
                    <div className={styles.challengeCardHead}>
                      <img src={iconSrc} alt="" className={styles.challengeIcon} />
                      <span>{headSpan}</span>
                    </div>
                    <div className={styles.challengeCardTitle}>
                      <p className={styles.challengeCardLabel}>{capitalLabel}</p>
                      <p className={styles.challengeCardAmount}>{amountLine}</p>
                    </div>
                    <div className={styles.challengeCardRows}>
                      <div className={styles.challengeRow}>
                        <span>{t("proptrading.profitTarget")}</span>
                        <span>{profitTargetDisplay}</span>
                      </div>
                      <div className={styles.challengeRow}>
                        <span>{t("proptrading.maxDrawdown")}</span>
                        <span>{maxDrawdownDisplay}</span>
                      </div>
                      <div className={styles.challengeRow}>
                        <span>{t("proptrading.timeLimit")}</span>
                        <span>{timeLimitDisplay}</span>
                      </div>
                    </div>
                    <p className={styles.challengePrice}>{challengePriceDisplay}</p>
                    <div className={styles.challengeBtnBlock}>
                      <button type="button" className={styles.challengeBtn} onClick={onStageBtnClick}>
                        {btnLabel}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 右侧切换箭头 */}
            <button
              type="button"
              className={`${styles.carouselArrow} ${styles.carouselArrowRight}`}
              onClick={() => setCurrentCardIndex((prev) => (prev < orderedStages.length - 1 ? prev + 1 : prev))}
              disabled={currentCardIndex === orderedStages.length - 1}
            >
              <img src={rightArrow} alt="next" />
            </button>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <p className={styles.sectionTitleMain}>{t("proptrading.sectionTitleFunding")}</p>
            <p className={styles.sectionTitleSub}>{t("proptrading.sectionSubFunding")}</p>
          </div>
          <div className={styles.fundingPath}>
            <div className={styles.fundingStep}>
              <div className={styles.fundingStepContent}>
                <p className={styles.fundingStepTitle}>{t("proptrading.stepTitleEvaluation")}</p>
                <p className={styles.fundingStepDesc}>{t("proptrading.stepDescEvaluation")}</p>
              </div>
              <img src={rule1} alt="" className={styles.fundingStepIcon} />
            </div>
            <div className={styles.fundingStep}>
              <div className={styles.fundingStepContent}>
                <p className={styles.fundingStepTitle}>{t("proptrading.stepTitleVerification")}</p>
                <p className={styles.fundingStepDesc}>{t("proptrading.stepDescVerification")}</p>
              </div>
              <img src={rule2} alt="" className={styles.fundingStepIcon} />
            </div>
            <div className={styles.fundingStep}>
              <div className={styles.fundingStepContent}>
                <p className={styles.fundingStepTitle}>{t("proptrading.stepTitleProfessional")}</p>
                <p className={styles.fundingStepDesc}>{t("proptrading.stepDescProfessional")}</p>
              </div>
              <img src={rule3} alt="" className={styles.fundingStepIcon} />
            </div>
          </div>
        </section>
      </div>
    </ProptradingLayout>
  );
};

export default ProptradingHomePage;
